import {Link} from 'react-router';
import {useTranslation} from 'react-i18next';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCodeBranch, faFlag} from '@fortawesome/free-solid-svg-icons';
import {
    SuggestedProblemEditStatusEnum,
    type SuggestedProblemEditMinimal,
    type DiffSummary,
} from '@types';
import {useSelector, canEditProblem} from '@utils';
import RankText from '../common/RankText';
import {
    SuggestionDeleteButton,
    SuggestionEntry,
    SuggestionTimestamp,
} from './SuggestionEntry';

export const ProblemSuggestionsList = ({
    items,
    showDeleteButton,
    showEditButton,
    showResolveButton,
    showViewButton,
    showSuggestedByColumn,
    showFlagColumn,
    isDeleting,
    onDelete,
    renderDiffSummary,
}: {
    items: SuggestedProblemEditMinimal[];
    showDeleteButton: boolean;
    showEditButton: boolean;
    showResolveButton: boolean;
    showViewButton: boolean;
    showSuggestedByColumn: boolean;
    showFlagColumn: boolean;
    isDeleting: boolean;
    onDelete: (id: number) => void;
    renderDiffSummary: (summary: DiffSummary | null) => string;
}) => {
    const {t} = useTranslation();
    const {user} = useSelector(state => state.auth);
    return (
        <ul className="suggestion-entries">
            {items.map(item => {
                const isOpen =
                    item.status === SuggestedProblemEditStatusEnum.OPEN;
                const showReview = showViewButton || showResolveButton;
                const canResolve = isOpen && canEditProblem(user, item.problem);
                const ownsSuggestion = item.user.id === user?.id;
                return (
                    <SuggestionEntry
                        key={item.id}
                        {...item}
                        metadata={
                            <>
                                <span>
                                    {t('common.author')}{' '}
                                    <Link
                                        to={`/users/${item.problem.author.name}`}>
                                        {item.problem.author.name}
                                    </Link>
                                </span>
                                <span>
                                    <RankText rank={item.problem.rank} />
                                </span>
                                {showSuggestedByColumn && (
                                    <span>
                                        {t('common.suggested_by')}{' '}
                                        <Link to={`/users/${item.user.name}`}>
                                            {item.user.name}
                                        </Link>
                                    </span>
                                )}
                                {item.createdAt && (
                                    <span>
                                        {t('suggestions.submitted')}{' '}
                                        <SuggestionTimestamp
                                            date={item.createdAt}
                                        />
                                    </span>
                                )}
                                {showFlagColumn && (
                                    <span title={t('common.flags')}>
                                        <FontAwesomeIcon
                                            icon={faFlag}
                                            aria-hidden="true"
                                        />{' '}
                                        {item.problem.flagStats
                                            .positiveFlagSum > 0 &&
                                            `+${item.problem.flagStats.positiveFlagSum} `}
                                        {item.problem.flagStats
                                            .negativeFlagSum < 0 &&
                                            item.problem.flagStats
                                                .negativeFlagSum}
                                        {item.problem.flagStats
                                            .positiveFlagSum === 0 &&
                                            item.problem.flagStats
                                                .negativeFlagSum === 0 &&
                                            '0'}
                                    </span>
                                )}
                            </>
                        }
                        summary={
                            item.diffSummary && (
                                <>
                                    <FontAwesomeIcon
                                        icon={faCodeBranch}
                                        aria-hidden="true"
                                    />
                                    <span>
                                        <span className="sr-only">
                                            {t(
                                                'suggestions.diff_summary.label'
                                            )}
                                            :{' '}
                                        </span>
                                        {renderDiffSummary(item.diffSummary)}
                                    </span>
                                </>
                            )
                        }
                        actions={
                            <>
                                {showReview && (
                                    <Link
                                        className={`btn ${canResolve ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        to={`/suggestions/${item.id}`}>
                                        {t(
                                            canResolve
                                                ? 'common.resolve'
                                                : 'common.view'
                                        )}
                                    </Link>
                                )}
                                {showEditButton &&
                                    ownsSuggestion &&
                                    (isOpen || !showReview) && (
                                        <Link
                                            className="btn btn-outline-secondary"
                                            to={
                                                isOpen
                                                    ? `/suggestions/${item.id}/edit`
                                                    : `/suggestions/${item.id}`
                                            }>
                                            {t(
                                                isOpen
                                                    ? 'common.edit'
                                                    : 'common.view'
                                            )}
                                        </Link>
                                    )}
                                {showDeleteButton &&
                                    isOpen &&
                                    item.id !== null && (
                                        <SuggestionDeleteButton
                                            disabled={isDeleting}
                                            onClick={() => {
                                                if (item.id !== null)
                                                    onDelete(item.id);
                                            }}
                                        />
                                    )}
                            </>
                        }
                    />
                );
            })}
        </ul>
    );
};
